interface AutoGrowElement extends HTMLElement {
  updateParameters?: (newMaxLines: number) => void;
  adjustAutoGrowHeight?: (didReflow?: boolean) => void;
  restoreToInitialState?: () => void;
  rows?: number;
}

export default class InputAutoGrow {
  /**
   * Initializes the auto-grow functionality for a given input element.
   * @param {HTMLElement} elem The input element to apply auto-grow functionality.
   * @param {number} maxLines The maximum number of lines the input can grow to.
   */
  static initAutoGrow(elem: AutoGrowElement, maxLines: number): void {
    const compStyle = getComputedStyle(elem);
    const lineHeight = parseFloat(compStyle.getPropertyValue('line-height'));

    let maxHeight = 0;

    // Update parameters that affect the functionality and visuals of the auto-growing input.
    elem.updateParameters = function (newMaxLines: number) {
      if (newMaxLines > 0) {
        // Cap the height to the number of lines specified in the input.
        maxHeight = lineHeight * newMaxLines;
      } else {
        maxHeight = 0;
      }
    }

    // Capture min and max height in closure to trigger height adjustment on element in the input.
    elem.adjustAutoGrowHeight = function (didReflow = false) {
      // Save scroll positions
      const scrollTops: [HTMLElement, number][] = [];
      let curElem: HTMLElement | null = elem;
      while (curElem && curElem.parentNode && curElem.parentNode instanceof HTMLElement) {
        if (curElem.parentNode.scrollTop) {
          scrollTops.push([curElem.parentNode, curElem.parentNode.scrollTop]);
        }
        curElem = curElem.parentNode;
      }

      elem.style.height = '0';

      if (didReflow) {
        elem.style.textAlign = '';
      }

      const minHeight = lineHeight * (elem.rows || 1);
      let newHeight = Math.max(minHeight, elem.scrollHeight);
      const initialOverflowY = elem.style.overflowY;
      if (maxHeight > 0 && newHeight > maxHeight) {
        // Content height exceeds the max height so we'll see a scrollbar.
        elem.style.overflowY = 'auto';
        newHeight = maxHeight;
      } else {
        // Scrollbar isn't needed and could either flash on resize or could appear
        // due to rounding inaccuracy in scrollHeight when the display is scaled.
        elem.style.overflowY = 'hidden';
      }

      elem.style.height = newHeight + "px";

      // Restore scroll positions.
      scrollTops.forEach(([node, scrollTop]) => {
        node.style.scrollBehavior = 'auto';
        node.scrollTop = scrollTop;
        node.style.scrollBehavior = '';
      });

      // Force another adjustment after the scrollbar is hidden to avoid an empty line
      if (!didReflow && initialOverflowY !== elem.style.overflowY && elem.style.overflowY === 'hidden' && elem.adjustAutoGrowHeight) {
        elem.style.textAlign = 'end'; // Change to something other than the default.
        elem.adjustAutoGrowHeight(true);
      }
    }

    // Terminate the ability to auto-grow and restore the input element back to its original state.
    elem.restoreToInitialState = function () {
      elem.removeEventListener('input', inputEventListener);
      elem.style.overflowY = '';
      elem.style.height = '';
    }

    // Wrapper function for input event listener.
    const inputEventListener = () => elem.adjustAutoGrowHeight && elem.adjustAutoGrowHeight();

    // Adjust height when input happens.
    elem.addEventListener('input', inputEventListener);

    // Wrapper function for resize event listener.
    const resizeEventListener = () => elem.adjustAutoGrowHeight && elem.adjustAutoGrowHeight();

    // Adjust height when the window resizes.
    window.addEventListener('resize', resizeEventListener as EventListener);

    // Initial parameters and height adjustment.
    elem.updateParameters(maxLines);
    elem.adjustAutoGrowHeight();
  }

  /**
   * Adjusts the height of the input element if auto-grow functionality is applied.
   * @param {HTMLElement} elem The input element to adjust height.
   */
  static adjustHeight(elem: AutoGrowElement): void {
    if (typeof elem.adjustAutoGrowHeight === 'function') {
      elem.adjustAutoGrowHeight();
    }
  }

  /**
   * Updates the parameters for the auto-grow functionality and adjusts the height.
   * @param {HTMLElement} elem The input element to update parameters.
   * @param {number} maxLines The new maximum number of lines the input can grow to.
   */
  static updateParams(elem: AutoGrowElement, maxLines: number): void {
    if (typeof elem.updateParameters === 'function') {
      elem.updateParameters(maxLines);
    }
    if (typeof elem.adjustAutoGrowHeight === 'function') {
      elem.adjustAutoGrowHeight();
    }
  }

  /**
   * Destroys the auto-grow functionality and restores the input element to its original state.
   * @param {HTMLElement} elem The input element to destroy auto-grow functionality.
   */
  static destroy(elem: AutoGrowElement | null): void {
    if (elem == null) {
      return;
    }

    const resizeEventListener = () => elem.adjustAutoGrowHeight && elem.adjustAutoGrowHeight();
    window.removeEventListener('resize', resizeEventListener as EventListener);

    if (typeof elem.restoreToInitialState === 'function') {
      elem.restoreToInitialState();
    }
  }
}
